import { useCallback } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { GroupedSummaryDto } from './types';
import {
  useSecuritySummaryQuery,
  useSummaryApisEnabled,
  useSummaryApisSelectionResolved,
} from './index';
import { useSummaryNoDataDiagnostics } from './useSummaryNoDataDiagnostics';

type LegacyQueryHook<TData, TParams extends object> = (
  params: TParams & { enabled?: boolean }
) => UseQueryResult<TData>;

type UseDualSecurityDataOptions<TData, TParams extends object> = {
  legacyParams: TParams;
  useLegacyQuery: LegacyQueryHook<TData, TParams>;
  mapSummary: (grouped: GroupedSummaryDto | undefined) => TData | undefined;
  diagnosticTag?: string;
};

type UseDualSecurityDataResult<TData> = {
  data: TData | undefined;
  isLoading: boolean;
  summaryEnabled: boolean;
  refetch: UseQueryResult<TData>['refetch'];
};

/**
 * Provides a unified data source for security tiles by switching between
 * legacy APIs and summary APIs behind the summary feature flag.
 */
export function useDualSecurityData<TData, TParams extends object>(
  options: UseDualSecurityDataOptions<TData, TParams>
): UseDualSecurityDataResult<TData> {
  const summaryEnabled = useSummaryApisEnabled();
  const selectionResolved = useSummaryApisSelectionResolved();
  const {
    data: groupedSummary,
    isLoading: isSummaryLoading,
    refetch: refetchSummary,
  } =
    useSecuritySummaryQuery();

  const {
    data: legacyData,
    isLoading: isLegacyLoading,
    refetch: refetchLegacy,
  } = options.useLegacyQuery({
    ...options.legacyParams,
    enabled: selectionResolved && !summaryEnabled,
  });

  const data = summaryEnabled
    ? options.mapSummary(groupedSummary)
    : legacyData;

  useSummaryNoDataDiagnostics({
    summaryEnabled,
    isSummaryLoading,
    groupedSummary,
    mappedData: data,
    legacyParams: options.legacyParams,
    domain: 'security',
    diagnosticTag: options.diagnosticTag,
  });

  const refetch = useCallback<UseQueryResult<TData>['refetch']>((options) => {
    const selectedRefetch = summaryEnabled ? refetchSummary : refetchLegacy;
    return selectedRefetch(options) as ReturnType<UseQueryResult<TData>['refetch']>;
  }, [summaryEnabled, refetchSummary, refetchLegacy]);

  return {
    data,
    isLoading: !selectionResolved || (summaryEnabled ? isSummaryLoading : isLegacyLoading),
    summaryEnabled,
    refetch,
  };
}
